import React, { useEffect } from 'react';
import { cityPages, locationAnswers, type CityPageData } from '../../data/locations';

const HOME_TITLE = 'Web Development, Branding, and App Development in Rochester and the Twin Cities | ThirtyFive Studio';
const SITE = 'https://thirtyfivestudio.com';

const setMeta = (selector: string, content: string) => {
  const tag = document.head.querySelector(selector);
  const previous = tag?.getAttribute('content') ?? '';
  tag?.setAttribute('content', content);
  return previous;
};

const CityPage = ({ city }: { city: CityPageData }) => {
  useEffect(() => {
    const title = `${city.title} | ThirtyFive Studio`;
    const url = `${SITE}/locations/${city.slug}`;
    const previousTitle = document.title;
    const previousDescription = setMeta('meta[name="description"]', city.intro);
    const previousOgTitle = setMeta('meta[property="og:title"]', title);
    const previousOgDescription = setMeta('meta[property="og:description"]', city.intro);
    const previousOgUrl = setMeta('meta[property="og:url"]', url);
    const previousTwitterTitle = setMeta('meta[name="twitter:title"]', title);
    const previousTwitterDescription = setMeta('meta[name="twitter:description"]', city.intro);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const previousCanonical = canonical.getAttribute('href') ?? '';
    canonical.setAttribute('href', url);
    document.title = title;

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'city-schema';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `What does ThirtyFive Studio do in ${city.name}?`,
          acceptedAnswer: { '@type': 'Answer', text: city.intro },
        },
        ...locationAnswers.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      ],
    });
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle || HOME_TITLE;
      setMeta('meta[name="description"]', previousDescription);
      setMeta('meta[property="og:title"]', previousOgTitle);
      setMeta('meta[property="og:description"]', previousOgDescription);
      setMeta('meta[property="og:url"]', previousOgUrl);
      setMeta('meta[name="twitter:title"]', previousTwitterTitle);
      setMeta('meta[name="twitter:description"]', previousTwitterDescription);
      canonical?.setAttribute('href', previousCanonical);
      schema.remove();
    };
  }, [city]);

  return (
    <main className="bg-black px-6 pb-20 pt-32 text-white md:px-10">
      <article className="mx-auto max-w-3xl">
        <p className="text-xs uppercase tracking-[0.16em] text-[#d0d0d0]">Locations</p>
        <h1 className="mt-6 text-4xl text-white md:text-6xl">{city.title}</h1>
        <p className="mt-6 text-base leading-relaxed text-[#d0d0d0]">{city.intro}</p>

        <div className="mt-12 space-y-8 border-t border-white/10 pt-10">
          {locationAnswers.map((item) => (
            <div key={item.question}>
              <h2 className="text-2xl text-white">{item.question}</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#d0d0d0] md:text-base">{item.answer}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <a href="/#contact" className="solid-button">
            Start a project
          </a>
          <a href="/#services" className="ghost-button">
            View services
          </a>
        </div>

        <div className="mt-16 border-t border-white/10 pt-8">
          <p className="text-xs uppercase tracking-[0.16em] text-[#d0d0d0]">Other cities</p>
          <ul className="mt-4 flex flex-wrap gap-3">
            {cityPages
              .filter((page) => page.slug !== city.slug)
              .map((page) => (
                <li key={page.slug}>
                  <a href={`/locations/${page.slug}`} className="text-sm text-white hover:text-[#d0d0d0]">
                    {page.name}
                  </a>
                </li>
              ))}
          </ul>
        </div>
      </article>
    </main>
  );
};

export default CityPage;
